import { CRAYO_ENTRY, money, crayoPlanTerms } from "@/lib/compare/crayo";
import type { CompareContent } from "@/lib/compare/types";

const hobby = CRAYO_ENTRY;

export const KLAP_CONTENT: CompareContent = {
  slug: "crayo-vs-klap",
  competitorSlug: "klap",
  title: "Crayo vs Klap",
  seoTitle: "Crayo vs Klap: Pricing, Metering and Features Compared",
  seoDescription:
    "Crayo vs Klap with dated prices. Both meter per clip. Klap adds 4K, dubbing and scheduling. Crayo takes 3-hour sources on the entry plan, ranks moments to approve first and puts a timeline editor on every plan.",
  cardVerdict: "Better than Crayo for free plan, 4K and scheduling, behind on source length and moment review.",
  bestFor: "Clippers with hour-long sources on an entry plan",
  datePublished: "2026-10-09",
  dateModified: "2026-10-25",
  verdict: `As of October 25, 2026, Crayo is the better pick if you clip hour-long podcasts and streams on an entry plan, want ranked moments to approve before cutting, and want a timeline editor with a face-aware camera track for ${money(hobby.monthly)} a month. Klap is the better pick if you want a free plan, the lowest yearly entry price among AI clippers, 4K export, AI dubbing or native scheduling. Both charge per clip generated. The evidence is below.`,
  pickCrayoIf: [
    "You clip hour-long sources on the entry plan: Crayo accepts up to 3 hours per upload on every plan, while Klap Basic caps each video at 45 minutes.",
    "You want to approve ranked moments before cutting: Find Viral Moments lists up to 50 and charges only for the clips you cut, and Clip Cutter ranges are free.",
    "You want to cancel from Settings in one click, with the plan active to the end of the cycle and nothing further charged.",
  ],
  pickThemIf: [
    "You want a free plan or the lowest yearly entry price among AI clippers: Klap Basic is $14 a month billed yearly for 100 clips, per klap.app/pricing on Oct 8, 2026. Crayo has no free plan.",
    "You need 4K downloads or AI dubbing into 29 languages. Both are on Klap Pro and Pro+. Crayo exports at 1080p and does not dub.",
    "You post straight from the tool: Klap schedules to TikTok, YouTube, Instagram and LinkedIn with analytics on every plan. Crayo has no scheduler.",
  ],
  wins: [
    {
      claim: "Crayo takes sources up to 3 hours on every plan; Klap caps source length by plan.",
      evidence:
        "Klap's pricing page, checked Oct 8, 2026, lists Basic at 10 videos of up to 45 minutes, Pro at 30 videos of up to 2 hours, and Pro+ at 100 videos of up to 3 hours. Crayo Hobby accepts 1 minute to 3 hours and up to 4 GB per upload.",
      href: "https://klap.app/pricing",
    },
    {
      claim: "Crayo lets you review ranked moments before cutting; Klap cuts in one pass.",
      evidence:
        "Klap's homepage says a one-minute video produces about 5 clips. Crayo's Find Viral Moments returns up to 50 ranked moments to approve first at 1 credit each, and Clip Cutter takes manual ranges for free.",
      href: "https://crayo.ai/features/auto-clip",
    },
    {
      claim: "Crayo cancels in one click from Settings and says so on its own pages.",
      evidence:
        "Klap's pricing page does not describe cancellation or refunds as of Oct 8, 2026. One dated Trustpilot review is linked in the billing section below.",
      href: "https://crayo.ai/refund-policy",
    },
  ],
  concessions: [
    {
      claim: "Klap has a free plan and the lowest yearly entry price among the AI clippers in this series.",
      evidence: `Klap's free plan is 1 video with no credit card. Basic is $14 a month billed yearly for 100 clips, per klap.app/pricing on Oct 8, 2026. Crayo has no free plan; Hobby is about ${money(hobby.yearlyPerMonth)} a month billed yearly for ${hobby.credits} credits, so Klap's yearly entry price is a few dollars a month lower, and the free plan is real.`,
      href: "https://klap.app/pricing",
    },
    {
      claim: "Klap exports 4K and dubs into 29 languages on Pro and Pro+.",
      evidence:
        "Klap Pro at $39 a month billed yearly lists 4K downloads and AI dubbing in 29 languages, checked Oct 8, 2026. Crayo exports at 1080p and has no dubbing.",
      href: "https://klap.app/pricing",
    },
    {
      claim: "Klap's API is on every plan and supports managed sub-users and embeds.",
      evidence:
        "Klap's API page documents a video-to-shorts endpoint with managed users and an embeddable editor. Crayo's API is also on every plan with 34 endpoints, but it issues one key per account with no sub-users.",
      href: "https://klap.app/api",
    },
    {
      claim: "Klap schedules posts and shows analytics on every plan.",
      evidence:
        "The pricing page lists unlimited social accounts and analytics on Basic, Pro and Pro+, and the homepage lists TikTok, YouTube, Instagram and LinkedIn. Crayo exports the file for you to post, and its View Tracker follows creators rather than your posts.",
      href: "https://klap.app/pricing",
    },
  ],
  typicalMonth: {
    scenario: "One 60-minute source a week, eight clips each: 240 source minutes and 32 clips a month.",
    crayo: {
      plan: "Hobby",
      cost: `${money(hobby.monthly)}/mo or about ${money(hobby.yearlyPerMonth)}/mo yearly`,
      usage: `32 of ${hobby.credits} workflow credits and about 32 of ${hobby.exportMinutes} export minutes; each source is under the 3-hour cap`,
      note: "Prices from the live plan table.",
    },
    them: {
      plan: "Pro",
      cost: "$79/mo or $39/mo yearly",
      usage:
        "32 of 300 clips and 4 of 30 videos. Basic's 100 clips would cover the volume, but Basic caps each video at 45 minutes and these sources run 60, so Pro is the first plan that accepts them",
      note: "Both prices read from the pricing page toggles in a browser on Oct 9, 2026.",
    },
  },
  test: {
    status: "scheduled",
    source: "A 45-minute two-person podcast recording owned by Crayo",
    plans: "Klap Basic and Crayo Clipper, same file, same day, defaults first then one tuned run each",
  },
  billingNote:
    "Klap's pricing page opens on the yearly toggle with a Save 50% label; the monthly toggle shows $29, $79 and $189, read in a browser on Oct 9, 2026. Rollover, refund and cancellation terms do not appear on the pricing page.",
  corrections: [
    {
      site: "Klap blog",
      url: "https://klap.app/blog/crayo-ai-alternatives",
      date: "2025-04-30",
      claim: "Crayo.ai currently does not offer a free plan.",
      reality: `Correct and still current as of October 25, 2026. Crayo has no free plan and no trial. The entry plan is Hobby at ${crayoPlanTerms(hobby)} for ${hobby.credits} credits. Twelve free browser tools at crayo.ai/features/free need no account and never upload the file.`,
      evidenceHref: "https://crayo.ai/features/free",
      evidenceLabel: "Free tools",
    },
    {
      site: "Klap blog",
      url: "https://klap.app/blog/crayo-ai-alternatives",
      date: "2025-04-30",
      claim: "Crayo.ai's tools do not work as advertised, citing laggy exports, pixelated output and subtitle issues.",
      reality:
        "The post names no test, date, file or product version. What Crayo advertises is documented on the linked page: AutoClip takes sources from 1 minute to 3 hours, cuts 1 to 20 clips per run with four layouts and ten switchable layers, exports at 1080p, and opens every clip in a timeline editor with 32 caption styles. The hands-on test above will add dated results.",
      evidenceHref: "https://crayo.ai/features/auto-clip",
      evidenceLabel: "AutoClip",
    },
    {
      site: "Klap blog",
      url: "https://klap.app/blog/crayo-ai-alternatives",
      date: "2025-04-30",
      claim: "The customer support system, primarily routed through Discord, is ineffective.",
      reality:
        "Crayo support runs on Discord 24 hours a day, 7 days a week, and by email at support@crayo.ai. Every page in this series carries a correction policy with a 48-hour promise.",
      evidenceHref: "https://discord.gg/crayo",
      evidenceLabel: "Crayo Discord",
    },
    {
      site: "Klap blog",
      url: "https://klap.app/blog/crayo-ai-alternatives",
      date: "2025-04-30",
      claim: "Voices are robotic and lacking polish.",
      reality:
        "Crayo's AI voiceover offers hundreds of ElevenLabs-backed voices on every plan, with voiceover minutes included in each plan. Listen to samples on the linked page and judge for yourself.",
      evidenceHref: "https://crayo.ai/features/ai-voiceover",
      evidenceLabel: "AI voiceover",
    },
  ],
  faqs: [
    {
      question: "Is Crayo cheaper than Klap?",
      answer: `It depends on billing cycle and source length. Crayo Hobby is ${crayoPlanTerms(hobby)} for ${hobby.credits} credits, and one credit is one finished clip. Klap Basic is $14 a month billed yearly for 100 clips, or $29 a month on monthly billing, from 10 videos of up to 45 minutes each. On monthly billing the two entry prices are the same; on yearly billing Klap is a few dollars a month lower; Crayo's quarterly plan sits between the two. Per clip, Hobby's ${hobby.credits} credits cost less than Basic's 100 at every term. If your sources run past 45 minutes, Klap moves you to Pro at $39 a month billed yearly.`,
    },
    {
      question: "Does Crayo have a free plan like Klap?",
      answer: `No. Crayo has no free plan and no trial, and Klap's April 2025 post is right about that. The entry plan is Hobby at ${money(hobby.monthly)} a month. What Crayo does offer free is twelve browser tools at crayo.ai/features/free, which need no account and never upload your file. Klap's free plan allows 1 video with no credit card, and a third-party review reports a watermark on the export.`,
    },
    {
      question: "How do Crayo and Klap meter usage?",
      answer:
        "The same way at the clip level. Klap's pricing page says you only pay for clips you generate, with 100, 300 or 1,000 clips a month by plan. Crayo charges one workflow credit per finished clip. The difference is the second cap: Klap also limits videos per month and minutes per video (10 videos up to 45 minutes on Basic), while Crayo limits export minutes and accepts up to 3 hours per upload on every plan.",
    },
    {
      question: "Which is better for long podcasts and streams, Crayo or Klap?",
      answer:
        "For episodes over 45 minutes, Crayo on the entry plan. Klap Basic accepts videos of up to 45 minutes, so a 60-minute episode needs Pro, which allows 2 hours. Crayo Hobby accepts up to 3 hours and 4 GB per upload, with speaker, streamer, split and full layouts for a podcast or a stream VOD. Both charge per clip, so the per-clip cost is similar; the plan you need is what changes. Crayo also returns up to 50 ranked moments to approve before cutting.",
    },
    {
      question: "Does Crayo auto-post like Klap?",
      answer:
        "No. Crayo exports the file and you post it yourself. Klap connects unlimited social accounts, schedules to TikTok, YouTube, Instagram and LinkedIn, and shows analytics on every plan, per its pricing page on Oct 8, 2026. Crayo's View Tracker is different: it follows Instagram, YouTube and TikTok creators to show posting cadence and viral outliers. If scheduling decides it, Klap wins that row.",
    },
    {
      question: "Does Crayo export 4K like Klap?",
      answer:
        "No. Crayo exports at up to 1080p on every plan. Klap lists HD downloads on Basic and 4K downloads on Pro and Pro+, which start at $39 a month billed yearly. For vertical clips posted to TikTok, Reels and Shorts, 1080p is the delivery format the platforms serve, but if you need a 4K master, Klap Pro provides it and Crayo does not. Crayo's editor does add zooms, camera moves, speed ramps and 32 caption styles before the 1080p export.",
    },
    {
      question: "Is Klap's review of Crayo accurate?",
      answer:
        "Partly. Klap's post, dated April 30, 2025, is correct that Crayo has no free plan. It also says Crayo's tools do not work as advertised, that Discord support is ineffective and that voices are robotic, without naming a test, a date or a version, and it quotes no prices. As of October 2026, support runs on Discord 24/7 and at support@crayo.ai, and the voiceover catalog holds hundreds of ElevenLabs-backed voices. The corrections box on this page answers each claim with the current fact and a link to the Crayo page that shows it.",
    },
    {
      question: "How does Klap compare with Opus Clip and Crayo?",
      answer:
        "Klap and Crayo both charge per clip generated. Opus Clip charges per minute of source uploaded, so a long episode that yields a few clips costs more there. Klap adds 4K, dubbing and scheduling; Opus Clip adds a free tier and team seats; Crayo adds 3-hour sources on the entry plan, ranked moments to approve before cutting and a timeline editor with 56 caption languages on every plan. Our Crayo vs Opus Clip page has the dated prices.",
    },
  ],
  changelog: [
    {
      date: "2026-10-09",
      text: "First published. Klap prices read in a real browser Oct 9, 2026: Basic $14/mo billed yearly or $29 monthly for 100 clips, Pro $39/mo yearly or $79 monthly with 4K and dubbing, Pro+ $94/mo yearly or $189 monthly.",
    },
    {
      date: "2026-10-25",
      text: `Crayo prices updated to the October 25, 2026 ladder: Hobby is ${crayoPlanTerms(hobby)}, with quarterly the default term at checkout. Verdicts and the corrections box re-checked at the new prices; competitor prices unchanged from the October 8 to 9 check.`,
    },
  ],
  relatedSpokes: ["crayo-vs-opus-clip", "crayo-vs-vizard", "crayo-vs-submagic"],
  featurePath: "/features/auto-clip",
  ctaHref: "https://crayo.ai/create/auto-clip",
  ctaLabel: "Clip a video now",
  targetQueries: [
    "klap alternative",
    "klap ai alternative",
    "klap app free alternative reddit",
    "crayo ai alternatives",
    "klap vs opus clip",
    "crayo vs klap",
  ],
};
